import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import { openPuckDrawer, closePuckDrawer } from "@/components/cms-puck/fields/puck-drawer-store";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import {
  getCatalogItemTypes,
  type SelectionItem,
  type SelectionItemType,
} from "@/lib/cms-puck/blocks/selection";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import {
  fetchCatalogItemsByIds,
  fetchCatalogList,
} from "@/api/cms-puck/catalog-picker-api";

/**
 * Custom field Puck pour le bloc `ItemSpotlight` : recherche & sélectionne UN
 * seul élément parmi TOUTES les tables d'œuvres (onglet par table). Stocke
 * `{ type, id }` (structurel). Réutilise le dispatcher `catalog-picker-api`.
 */

function labelFor(type: SelectionItemType): string {
  return getCatalogItemTypes().find((t) => t.type === type)?.label ?? type;
}

function Thumb({
  url,
  alt,
  size = 96,
}: {
  url?: string;
  alt?: string;
  size?: number;
}) {
  if (!url) {
    return (
      <div
        className="flex items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400"
        style={{ width: size, height: size }}
      >
        —
      </div>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt={alt ?? ""}
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: "cover" }}
      className="rounded bg-slate-100"
    />
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

/** Contenu du drawer (monté dans le HOST → state stable). */
export function ItemSpotlightPickerBody({
  selected,
  onPick,
  allowedTypes,
}: {
  selected: SelectionItem | undefined;
  onPick: (item: SelectionItem) => void;
  /** Si défini, limite les onglets du picker (ex. CatalogueItemDetail). */
  allowedTypes?: SelectionItemType[];
}) {
  const cmsNew = useCmsNew();
  const types = allowedTypes?.length
    ? getCatalogItemTypes().filter((t) => allowedTypes.includes(t.type))
    : getCatalogItemTypes();
  const [type, setType] = useState<SelectionItemType>(
    () => types[0]?.type ?? "artwork",
  );
  const [search, setSearch] = useState("");

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["catalog-picker", type, search.trim()],
    queryFn: () => fetchCatalogList(type, search),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-3 pt-4">
        {/* Onglets masqués s'il n'y a qu'un type (ex. déjà choisi via Œuvres/Multiples). */}
        {types.length > 1 ? (
          <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
            {types.map((t) => (
              <TabButton
                key={t.type}
                active={type === t.type}
                onClick={() => setType(t.type)}
              >
                {t.label}
              </TabButton>
            ))}
          </div>
        ) : null}
        <SearchBar
          search={search}
          onChangeSearch={setSearch}
          placeholder={cmsNew("cms.new.auto.placeholder.rechercher", "Rechercher…")}
        />
        {isLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-slate-500">
            {search.trim()
              ? cmsPhrase(cmsNew, "pickerNoResults")
              : cmsPhrase(cmsNew, "pickerNoItems")}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {results.map(({ id, title, registrationNumber, url }) => {
              const isSel =
                selected?.type === type && selected?.id === id;
              const altText = [registrationNumber, title]
                .filter(Boolean)
                .join(" — ");
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onPick({ type, id })}
                  className={`group flex flex-col items-center gap-0.5 rounded border p-1 ${
                    isSel
                      ? "border-primary ring-2 ring-primary/40"
                      : "border-transparent hover:border-slate-300"
                  }`}
                  title={altText}
                >
                  <Thumb url={url} alt={altText} size={96} />
                  {registrationNumber ? (
                    <span className="line-clamp-1 w-full text-center text-[10px] font-medium text-slate-600">
                      {registrationNumber}
                    </span>
                  ) : null}
                  {title ? (
                    <span className="line-clamp-1 w-full text-center text-[11px] text-slate-500">
                      {title}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
    </div>
  );
}

export function ItemSpotlightPickerField({
  value,
  onChange,
  allowedTypes,
}: {
  value: SelectionItem | undefined;
  onChange: (value: SelectionItem | null) => void;
  /** Limite les types sélectionnables (ex. œuvres et multiples uniquement). */
  allowedTypes?: SelectionItemType[];
}) {
  const cmsNew = useCmsNew();
  const item = value && value.id ? value : undefined;

  const { data: resolved = [] } = useQuery({
    queryKey: ["catalog-picker-selected", item?.type, item?.id],
    queryFn: () =>
      item ? fetchCatalogItemsByIds(item.type, [item.id]) : Promise.resolve([]),
    enabled: !!item,
  });
  const chip = resolved[0];
  const chipLabel = chip
    ? [chip.registrationNumber, chip.title].filter(Boolean).join(" — ") ||
      `${labelFor(item!.type)} ${item!.id.slice(0, 6)}`
    : item
      ? `${labelFor(item.type)} ${item.id.slice(0, 6)}`
      : "";

  const openPicker = () =>
    openPuckDrawer({
      title: cmsNew("cms.new.auto.title.choisir_un_element", "Choisir un élément"),
      maxWidth: "sm:max-w-2xl",
      node: (
        <ItemSpotlightPickerBody
          selected={item}
          allowedTypes={allowedTypes}
          onPick={(next) => {
            onChange(next);
            closePuckDrawer();
          }}
        />
      ),
    });

  return (
    <div className="space-y-2 p-1">
      <div className="text-xs font-medium text-slate-600">{cmsNew("cms.new.auto.text.element", "Élément")}</div>
      {item ? (
        <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 py-1 pl-1 pr-2">
          <Thumb url={chip?.url} alt={chipLabel} size={32} />
          <span className="max-w-[180px] truncate text-xs text-slate-600">
            {chipLabel}
          </span>
          <span className="rounded bg-slate-200 px-1 text-[9px] uppercase text-slate-500">
            {labelFor(item.type)}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
            title={cmsNew("cms.new.auto.title.retirer", "Retirer")}
            aria-label={cmsNew("cms.new.auto.label.retirer_lelement", "Retirer l'élément")}
          >
            <XIcon size={14} />
          </button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">{cmsNew("cms.new.auto.text.aucun_element_selectionne", "Aucun élément sélectionné.")}</p>
      )}
      <Button type="button" variant="outline" size="sm" onClick={openPicker}>
        {item ? cmsPhrase(cmsNew, "changeElement") : cmsPhrase(cmsNew, "chooseElement")}
      </Button>
    </div>
  );
}
